import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";
import { Link } from "react-router-dom";
import { GraduationCap, BookOpen, Users, Sparkles } from "lucide-react";

const PROGRAMS = [
  { icon: GraduationCap, title: "Religious School (K-7)", desc: "Sunday mornings, our school nurtures Jewish identity through Hebrew, history, holidays, prayer, and values. Small classes mean every child is seen - and it's included FREE with your family's membership." },
  { icon: BookOpen, title: "Adult Hebrew", desc: "Rabbi Fanwick's signature classes use her own texts - Hebrew 101 (Biblical) and Hebrew 102 (Prayer) - for adult learners at every level." },
  { icon: Users, title: "Torah Study", desc: "Open conversation about the weekly parashah every Saturday morning at 9:30 AM. No prep needed - drop in any week." },
  { icon: Sparkles, title: "A Journey of Discovery", desc: "A personalized conversion program for those exploring Jewish life. Rabbi Fanwick walks alongside each student." },
];

const IMG_SCHOOL = "/wp-content/uploads/2023/09/botom-pic2.jpg";

export default function Education() {
  return (
    <>
      <SEOHead title="Hebrew School & Religious School in Melbourne FL" description="Religious school and Hebrew school at Temple Israel of Brevard, serving Melbourne, FL and Brevard County. K-7 youth education, adult Hebrew, Torah study, and Path to Judaism for those becoming Jewish." path="/education" />
      <PageHero eyebrow="Lifelong Jewish Learning" title="Hebrew school, religious school, and adult learning in Brevard County." subtitle="From aleph-bet to b'nei mitzvah and beyond. Religious school in Melbourne, FL and Hebrew school for adults too - there's a class with your name on it at TIOB." />

      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-2">
            <div className="relative">
              <div aria-hidden="true" className="absolute -inset-3 bg-[var(--honey)]/20 rounded-3xl rotate-2" />
              <img src={IMG_SCHOOL} alt="Religious school learning" className="relative rounded-3xl shadow-xl w-full object-cover h-96" />
            </div>
          </div>
          <div className="lg:col-span-3">
            <p className="eyebrow mb-4">★ Our Approach</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              Joyful, accessible, deeply rooted.
            </h2>
            <p className="mt-6 text-[var(--ink-soft)] leading-relaxed text-lg">
              We believe Jewish education is not just about facts - it's about formation.
              Our Religious School (grades K-7) meets Sunday mornings and integrates Hebrew,
              holidays, history, prayer, and values into a warm, hands-on experience.
            </p>
            <p className="mt-4 text-[var(--ink-soft)] leading-relaxed">
              Adult learners thrive too. Rabbi Karen Fanwick - author of <em>Hebrew 101</em> and{" "}
              <em>Hebrew 102</em> - has developed methods specifically for adults that strip away
              the extraneous and get straight to the joy of reading Hebrew.
            </p>
            <Link to="/contact" className="btn-primary mt-8" data-testid="education-contact-cta">
              Enroll or learn more
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[var(--cream)] py-20 lg:py-28 border-y border-[var(--line-warm)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] mb-12">Programs & Classes</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {PROGRAMS.map((p) => (
              <div key={p.title} className="card-soft p-7 bg-white" data-testid={`program-${p.title.split(" ")[0].toLowerCase()}`}>
                <div className="w-14 h-14 rounded-2xl bg-[var(--blue)]/10 text-[var(--blue-deep)] flex items-center justify-center mb-5">
                  <p.icon size={24} />
                </div>
                <h3 className="font-display text-2xl text-[var(--blue-ink)]">{p.title}</h3>
                <p className="mt-3 text-[var(--ink-soft)] text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
